

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import BackButton from '../../../components/common/BackButton';

const G = '#1A6B3C';
const CAT_COLORS = {
  vegetables: { bg: '#EAF3DE', color: '#3B6D11' },
  flowers:    { bg: '#FBEAF0', color: '#72243E' },
  fruits:     { bg: '#FEE2E2', color: '#991B1B' },
  seedlings:  { bg: '#E6F1FB', color: '#185FA5' },
  herbs:      { bg: '#FEF9C3', color: '#854D0E' },
  organic:    { bg: '#EAF3DE', color: '#3B6D11' },
};

export default function NearbyNurseriesPage() {
  const navigate = useNavigate();
  const [nurseries, setNurseries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');

  useEffect(() => { loadNearbyNurseries(); }, []);

  const loadNearbyNurseries = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nursery');
      if (data.success) setNurseries(data.nurseries || []);
    } catch (err) {
      console.log(err);
    } finally { setLoading(false); }
  };

  const FILTERS = ['All', 'Verified', 'Open Now'];

  const filtered = nurseries.filter(n => {
    const matchSearch = !search ||
      n.name?.toLowerCase().includes(search.toLowerCase()) ||
      n.city?.toLowerCase().includes(search.toLowerCase()) ||
      n.ownerName?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All'
      || (filter === 'Verified' && n.isVerified)
      || (filter === 'Open Now' && n.isOpen);
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: 90 }}>

      {/* Header */}
      <div style={{ background: G, padding: '14px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <BackButton />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'white' }}>Nearby Nurseries</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.7)' }}>
              {loading ? 'Finding nurseries...' : `${filtered.length} nurseries found`}
            </div>
          </div>
          <button onClick={() => navigate('/nursery/list')}
            style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: 'white', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            View All
          </button>
        </div>

        {/* Search */}
        <div style={{ background: 'white', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, border: 'none' }}>
          <span style={{ color: '#9CA3AF', fontSize: 16 }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or city..."
            style={{ border: 'none', background: 'transparent', color: '#374151', fontSize: 13, flex: 1, outline: 'none' }}
          />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>
          )}
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 6, paddingBottom: 12 }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '5px 14px', borderRadius: 20, border: filter === f ? 'none' : '1px solid rgba(255,255,255,.3)', background: filter === f ? 'white' : 'transparent', color: filter === f ? G : 'rgba(255,255,255,.85)', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 14px' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ background: 'white', borderRadius: 16, height: 100, border: '0.5px solid #E5E7EB', opacity: 0.5 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#9CA3AF' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🪴</div>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
              {search ? `No nurseries found for "${search}"` : 'No nurseries nearby'}
            </div>
            <div style={{ fontSize: 13, marginBottom: 20 }}>Try a different search or check back later</div>
            {search && (
              <button onClick={() => setSearch('')}
                style={{ background: G, color: 'white', border: 'none', borderRadius: 20, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Show all nurseries
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(n => (
              <div key={n._id}
                style={{ background: 'white', borderRadius: 16, overflow: 'hidden', border: '0.5px solid #E5E7EB' }}>

                {/* Cover photo row */}
                <div style={{ display: 'flex', gap: 0 }}>
                  {/* Nursery photo */}
                  <div style={{ width: 90, height: 90, flexShrink: 0, background: '#EAF3DE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, overflow: 'hidden' }}>
                    {n.photo
                      ? <img src={n.photo} alt={n.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : ''}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{n.name}</span>
                          {n.isVerified && (
                            <span style={{ background: '#DCFCE7', color: '#166534', fontSize: 9, padding: '2px 6px', borderRadius: 8, fontWeight: 700 }}>✓ Verified</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                          👤 {n.ownerName}
                        </div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>
                           {n.city || n.address?.split(',')[0] || 'Location not set'}
                        </div>
                      </div>
                    </div>

                    {/* Specialties */}
                    {n.specialties?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {n.specialties.slice(0, 3).map(s => {
                          const sc = CAT_COLORS[s.toLowerCase()] || CAT_COLORS.organic;
                          return (
                            <span key={s} style={{ background: sc.bg, color: sc.color, fontSize: 9, padding: '2px 7px', borderRadius: 10, textTransform: 'capitalize', fontWeight: 500 }}>
                              {s}
                            </span>
                          );
                        })}
                        {n.specialties.length > 3 && (
                          <span style={{ background: '#F3F4F6', color: '#6B7280', fontSize: 9, padding: '2px 7px', borderRadius: 10 }}>
                            +{n.specialties.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action row */}
                <div style={{ padding: '10px 14px', borderTop: '0.5px solid #F3F4F6', display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => navigate(`/nursery/${n._id}`)}
                    style={{ flex: 1, background: G, color: 'white', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    View Plants →
                  </button>
                  <a href={`tel:+91${n.mobile}`}
                    style={{ width: 42, height: 42, background: '#EAF3DE', color: G, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, textDecoration: 'none', flexShrink: 0 }}>
                    📞
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}